
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useUserRoles } from "@/hooks/useUserRoles";
import { useSupabaseUser } from "@/hooks/use-supabase-user";
import { GuardSearchDialog } from "../../guards/components/GuardSearchDialog";
import { Guard } from "../../guards/types";
import { DropdownMenu, DropdownMenuCheckboxItem, DropdownMenuContent, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Loader2 } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";

interface EquipmentType {
  id: string;
  name: string;
  base_price: number;
}

interface EquipmentItem {
  equipmentTypeId: string;
  equipmentTypeName: string;
  basePrice: number;
  quantity: number;
  isSelected: boolean;
}

// Updated interface to match what we actually have in the database
interface EquipmentInventoryItem {
  id: string;
  name: string;
  base_price: number;
}

// Define schema outside the component to avoid recursive type issues
const assignmentSchema = z.object({
  guardId: z.string().min(1, "Please select a guard"),
  notes: z.string().optional(),
  equipmentItems: z.array(
    z.object({
      equipmentTypeId: z.string(),
      equipmentTypeName: z.string(),
      basePrice: z.number(),
      quantity: z.number().int().positive("Quantity must be positive"),
      isSelected: z.boolean(),
    })
  ).refine(
    (items) => items.some(item => item.isSelected),
    {
      message: "Select at least one equipment item",
    }
  ),
});

// Define form values type
type AssignmentFormValues = z.infer<typeof assignmentSchema>;

export const GuardEquipmentAssignmentTab = () => {
  const [guards, setGuards] = useState<Guard[]>([]);
  const [selectedGuard, setSelectedGuard] = useState<Guard | null>(null);
  const [equipmentTypes, setEquipmentTypes] = useState<EquipmentType[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [existingEquipment, setExistingEquipment] = useState<EquipmentInventoryItem[]>([]);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [assignedEquipment, setAssignedEquipment] = useState<{name: string; quantity: number; basePrice: number}[]>([]);
  const { toast } = useToast();
  const { isAdmin } = useUserRoles();
  const { user } = useSupabaseUser();
  
  const form = useForm<AssignmentFormValues>({
    resolver: zodResolver(assignmentSchema),
    defaultValues: {
      guardId: "",
      notes: "",
      equipmentItems: [],
    },
  });
  
  useEffect(() => {
    fetchGuards();
    fetchEquipmentTypes();
    fetchExistingEquipment();
  }, []);
  
  const fetchGuards = async () => {
    try {
      setIsLoading(true);
      
      // Get all guards/managers that this admin can access
      let query = supabase
        .from('profiles')
        .select('id, first_name, last_name, full_name, code, mobile_phone, status, Role')
        .neq('status', 'inactive')
        .neq('status', 'archived')
        .or('Role.eq.guard,Role.eq.manager,Role.is.null')
        .order('last_name');

      const { data, error } = await query;
        
      if (error) throw error;
      
      // Filter out admins and super admins
      if (data && data.length > 0) {
        const profileIds = data.map(p => p.id);
        const { data: adminRoles } = await supabase
          .from('user_roles')
          .select('user_id')
          .in('user_id', profileIds)
          .in('role', ['admin', 'super_admin']);
        
        const adminIds = new Set(adminRoles?.map(r => r.user_id) || []);
        const nonAdminGuards = data.filter(p => !adminIds.has(p.id));
        
        const guardsWithDefaults = nonAdminGuards.map(guard => ({
          ...guard,
          hiring_date: "", // Provide default value
          date_of_birth: "", // Provide default value
          license_expiry_date: "", // Provide default value
        })) as Guard[];
        
        setGuards(guardsWithDefaults);
      } else {
        setGuards([]);
      }
    } catch (error: any) {
      toast({
        title: "Error",
        description: `Failed to load guards: ${error.message}`,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  const fetchExistingEquipment = async () => {
    try {
      const { data, error } = await supabase
        .from('equipment_types')
        .select('id, name, base_price');
        
      if (error) throw error;
      
      setExistingEquipment(data || []);
      console.log("Fetched existing equipment:", data);
    } catch (error: any) {
      console.error("Error fetching existing equipment:", error);
      toast({
        title: "Error",
        description: `Failed to load equipment inventory: ${error.message}`,
        variant: "destructive",
      });
    }
  };
  
  const fetchEquipmentTypes = async () => {
    try {
      setIsLoading(true);
      const { data, error } = await supabase
        .from('equipment_types')
        .select('id, name, base_price')
        .order('name');
        
      if (error) throw error;
      
      setEquipmentTypes(data || []);
      console.log("Fetched equipment types:", data);
      
      // Set up form values for equipment types
      const equipmentItems: EquipmentItem[] = (data || []).map(type => ({
        equipmentTypeId: type.id,
        equipmentTypeName: type.name,
        basePrice: type.base_price,
        quantity: 1,
        isSelected: false,
      }));
      
      form.setValue('equipmentItems', equipmentItems);
      
    } catch (error: any) {
      toast({
        title: "Error",
        description: `Failed to load equipment types: ${error.message}`,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectGuard = async (guard: Guard) => {
    setSelectedGuard(guard);
    form.setValue('guardId', guard.id);
    await fetchAssignedEquipment(guard.id);
  };

  const fetchAssignedEquipment = async (guardId: string) => {
    try {
      const { data, error } = await supabase
        .from('guard_equipment')
        .select(`
          quantity,
          equipment_types!guard_equipment_equipment_type_id_fkey (
            name,
            base_price
          )
        `)
        .eq('guard_id', guardId)
        .is('return_date', null);

      if (error) throw error;

      const formatted = (data || []).map((item: any) => ({
        name: item.equipment_types?.name || 'Unknown',
        quantity: item.quantity,
        basePrice: item.equipment_types?.base_price || 0
      }));

      setAssignedEquipment(formatted);
    } catch (error: any) {
      console.error('Error fetching assigned equipment:', error);
      setAssignedEquipment([]);
    }
  };
  
  const onSubmit = async (values: AssignmentFormValues) => {
    try {
      setIsSubmitting(true);
      setErrorMessage(null);
      
      if (!user?.id) {
        toast({
          title: "Authentication Error",
          description: "You must be logged in to assign equipment",
          variant: "destructive",
        });
        return;
      }
      
      const selectedEquipment = values.equipmentItems.filter(item => item.isSelected);
      console.log("Processing selected equipment:", selectedEquipment);
      
      if (selectedEquipment.length === 0) {
        setErrorMessage("Please select at least one equipment item");
        return;
      }
      
      // Create assignments directly using equipment_types table
      const guardAssignments = selectedEquipment.map(item => ({
        guard_id: values.guardId,
        equipment_type_id: item.equipmentTypeId, // Use equipment type ID directly
        quantity: item.quantity,
        notes: values.notes || null,
        assigned_by: user.id
      }));
      
      console.log("Creating guard equipment assignments:", guardAssignments);
      
      const { error: assignmentError } = await supabase
        .from('guard_equipment')
        .insert(guardAssignments);
        
      if (assignmentError) {
        setErrorMessage(`Failed to assign equipment to guard: ${assignmentError.message}`);
        console.error("Error assigning equipment to guard:", assignmentError);
      } else {
        // Success! Equipment assigned
        await fetchExistingEquipment();
        
        toast({
          title: "Equipment assigned",
          description: `Successfully assigned ${guardAssignments.length} equipment items to the guard`,
        });
        
        // Reset form
        form.reset({
          guardId: "",
          notes: "",
          equipmentItems: equipmentTypes.map(type => ({
            equipmentTypeId: type.id,
            equipmentTypeName: type.name,
            basePrice: type.base_price,
            quantity: 1,
            isSelected: false,
          })),
        });
        
        setSelectedGuard(null);
      }
    } catch (err: any) {
      console.error("Error in onSubmit:", err);
      toast({
        title: "Error",
        description: `Failed to assign equipment: ${err.message}`,
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const toggleEquipmentSelection = (index: number) => {
    const currentItems = form.getValues('equipmentItems');
    currentItems[index].isSelected = !currentItems[index].isSelected;
    form.setValue('equipmentItems', [...currentItems]);
  };

  const selectedItemsCount = form.watch('equipmentItems')?.filter(item => item.isSelected)?.length || 0;
  
  return (
    <div className="space-y-4">
      <h2 className="text-xl font-semibold">Assign Equipment to Guards</h2>
      
      {errorMessage && (
        <Alert variant="destructive" className="mb-4">
          <AlertDescription>{errorMessage}</AlertDescription>
        </Alert>
      )}
      
      {isLoading ? (
        <div className="flex items-center justify-center p-4">
          <Loader2 className="h-6 w-6 animate-spin mr-2" />
          <span>Loading...</span>
        </div>
      ) : (
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <FormField
              control={form.control}
              name="guardId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Select Guard</FormLabel>
                  <div className="flex flex-col space-y-2">
                    <GuardSearchDialog
                      guards={guards}
                      onSelectGuard={handleSelectGuard}
                      isLoading={isLoading}
                      buttonLabel={selectedGuard ? `${selectedGuard.last_name} ${selectedGuard.first_name}` : "Select Guard"}
                    />
                    {field.value && !selectedGuard && (
                      <div className="text-sm text-muted-foreground">
                        Guard ID: {field.value}
                      </div>
                    )}
                    <FormMessage />
                  </div>
                </FormItem>
              )}
            />

            {/* Show currently assigned equipment */}
            {selectedGuard && assignedEquipment.length > 0 && (
              <Card className="bg-blue-50 border-blue-200">
                <CardContent className="p-4">
                  <h3 className="font-semibold mb-3 text-blue-900">Χρεωμένο στον φύλακα:</h3>
                  <div className="space-y-2">
                    {assignedEquipment.map((item, idx) => (
                      <div key={idx} className="flex justify-between items-center text-sm">
                        <span className="font-medium">{item.name}</span>
                        <span className="text-blue-700">Ποσότητα: {item.quantity}</span>
                      </div>
                    ))}
                    <div className="pt-2 border-t border-blue-300 mt-2">
                      <div className="flex justify-between font-semibold text-blue-900">
                        <span>Σύνολο Εξοπλισμού:</span>
                        <span>{assignedEquipment.reduce((sum, item) => sum + item.quantity, 0)} τεμάχια</span>
                      </div>
                      <div className="flex justify-between font-semibold text-blue-900 mt-1">
                        <span>Συνολική Αξία:</span>
                        <span>€{assignedEquipment.reduce((sum, item) => sum + (item.quantity * item.basePrice), 0).toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
            
            <div>
              <FormLabel className="block mb-2">Select Equipment Types</FormLabel>
              
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" className="w-full justify-between">
                    Select Equipment Types
                    {selectedItemsCount > 0 && (
                      <span className="ml-2 rounded-full bg-primary text-primary-foreground px-2 py-0.5 text-xs">
                        {selectedItemsCount}
                      </span>
                    )}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-full md:w-[400px]" align="start">
                  <DropdownMenuLabel>Available Equipment Types</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {equipmentTypes.length === 0 ? (
                    <div className="p-2 text-sm text-muted-foreground">No equipment types available</div>
                  ) : (
                    <div className="max-h-[300px] overflow-auto">
                      {equipmentTypes.map((type, index) => {
                        const isSelected = form.watch(`equipmentItems.${index}.isSelected`);
                        return (
                          <DropdownMenuCheckboxItem
                            key={type.id}
                            checked={isSelected}
                            onCheckedChange={() => toggleEquipmentSelection(index)}
                          >
                            <div className="flex flex-col">
                              <span>{type.name}</span>
                              <span className="text-xs text-muted-foreground">Base Price: €{type.base_price.toFixed(2)}</span>
                            </div>
                          </DropdownMenuCheckboxItem>
                        );
                      })}
                    </div>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
              
              {form.formState.errors.equipmentItems?.message && (
                <p className="text-sm text-destructive mt-2">{form.formState.errors.equipmentItems.message}</p>
              )}
              
              {/* Display selected items with quantity controls */}
              {selectedItemsCount > 0 && (
                <Card className="mt-4">
                  <CardContent className="p-4">
                    <h3 className="font-medium mb-2">Selected Equipment</h3>
                    <div className="space-y-3">
                      {form.watch('equipmentItems')
                        .filter(item => item.isSelected)
                        .map((item, originalIndex) => {
                          // Find the original index in the full array
                          const index = form.getValues('equipmentItems').findIndex(
                            i => i.equipmentTypeId === item.equipmentTypeId
                          );
                          
                          // Find corresponding item in existing equipment
                          const inventoryItem = existingEquipment.find(
                            e => e.id === item.equipmentTypeId
                          );
                          
                          // Default to 100 if no inventory constraints
                          const maxQuantity = 100;
                          
                          return (
                            <div key={item.equipmentTypeId} className="flex items-center space-x-4 py-2 border-b last:border-b-0">
                              <div className="flex-grow">
                                <p className="font-medium">{item.equipmentTypeName}</p>
                                <p className="text-sm text-muted-foreground">
                                  Base Price: €{item.basePrice.toFixed(2)}
                                </p>
                              </div>
                              <FormField
                                control={form.control}
                                name={`equipmentItems.${index}.quantity`}
                                render={({ field }) => (
                                  <FormItem className="w-20 flex-shrink-0">
                                    <FormControl>
                                      <Input
                                        type="number"
                                        min="1"
                                        max={maxQuantity}
                                        {...field}
                                        onChange={(e) => {
                                          // Parse value and ensure it's within valid range
                                          const value = parseInt(e.target.value) || 1;
                                          const validValue = Math.min(Math.max(1, value), maxQuantity);
                                          form.setValue(
                                            `equipmentItems.${index}.quantity`, 
                                            validValue
                                          );
                                        }}
                                        className="w-20"
                                      />
                                    </FormControl>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0"
                                onClick={() => toggleEquipmentSelection(index)}
                              >
                                &times;
                              </Button>
                            </div>
                          );
                        })}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
            
            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Notes (Optional)</FormLabel>
                  <FormControl>
                    <Textarea placeholder="Add any notes about this assignment" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <Button 
              type="submit" 
              className="w-full"
              disabled={!selectedGuard || selectedItemsCount === 0 || isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Assigning Equipment...
                </>
              ) : (
                "Assign Equipment"
              )}
            </Button>
          </form>
        </Form>
      )}
    </div>
  );
};
